import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AuthService} from '@src/app/core/services/auth.service';
import {PermissionsService} from '@src/app/core/services/permissions.service';
import {StorageService} from '@src/app/core/services/storage.service';
import {UsersService} from '@src/app/admin/users/services/users.service';
import {BaseService} from '@src/app/shared/services/base.service';
import {environment} from '@src/environments/environment';
import {Observable} from 'rxjs';

interface DeveloperSessionSnapshot {
    user: any;
    tenant: any;
    refreshToken: string | null;
    credits: number | null;
    walkthroughs: any[];
    roles: string[] | string | null;
    permissions: string[] | string | null;
}

@Injectable({
    providedIn: 'root'
})
export class ImpersonationService extends BaseService
{
    private readonly impersonatingKey = 'impersonating';
    private readonly devTokenKey = 'devToken';
    private readonly impersonatedUserNameKey = 'impersonatedUserName';
    private readonly impersonatedUserEmailKey = 'impersonatedUserEmail';
    private readonly devSessionKey = 'devSession';

    public constructor(
        private httpClient: HttpClient,
        private authService: AuthService,
        private permissionsService: PermissionsService,
        private usersService: UsersService,
        private storageService: StorageService,
    )
    {
        super();
    }

    public impersonate(userId: number): Observable<any>
    {
        return this.httpClient.post(`${this.apiUrl}/impersonate/${userId}`, {});
    }

    public stop(tokenOverride?: string): Observable<any>
    {
        const headers = tokenOverride
            ? {
                Authorization: `Bearer ${tokenOverride}`,
                'X-Client-Ident': environment.webEnum,
                'X-Skip': 'true',
            }
            : {};

        return this.httpClient.post(`${this.apiUrl}/impersonate/stop`, {}, { headers });
    }

    public get isImpersonating(): boolean
    {
        return this.storageService.read(this.impersonatingKey) === 'true';
    }

    public get devToken(): string
    {
        return this.storageService.read(this.devTokenKey) ?? '';
    }

    public get impersonatedUserName(): string
    {
        return this.storageService.read(this.impersonatedUserNameKey) ?? '';
    }

    public get impersonatedUserEmail(): string
    {
        return this.storageService.read(this.impersonatedUserEmailKey) ?? '';
    }

    public startSession(response: any): void
    {
        // Capture the developer's current state BEFORE authenticateUser() overwrites it
        const devToken = this.authService.accessToken ?? null;
        const devSession = (devToken && !this.devToken) ? JSON.stringify(this.captureDeveloperSession()) : null;

        // authenticateUser sets impersonated user's auth state and clears any stale impersonation keys
        this.authService.authenticateUser(response);

        // Write impersonation keys AFTER authenticateUser so they are not wiped
        if (devToken && !this.storageService.read(this.devTokenKey)) {
            this.storageService.write(this.devTokenKey, devToken);
        }
        if (devSession) {
            this.storageService.write(this.devSessionKey, devSession);
        }

        this.storageService.write(this.impersonatingKey, 'true');
        this.storageService.write(this.impersonatedUserNameKey, response?.data?.user?.name ?? '');
        this.storageService.write(this.impersonatedUserEmailKey, response?.data?.user?.email ?? '');
    }

    public endSession(): void
    {
        if (this.devToken) {
            this.authService.accessToken = this.devToken;
        }

        const snapshot = this.getDeveloperSessionSnapshot();

        if (snapshot?.refreshToken !== undefined) {
            this.authService.refreshToken = snapshot.refreshToken as any;
        }

        if (snapshot?.user) {
            this.authService.user = snapshot.user;
            this.usersService.user = snapshot.user;
        }

        if (snapshot?.tenant) {
            this.authService.tenant = snapshot.tenant;
        }

        if (snapshot?.credits !== null && snapshot?.credits !== undefined) {
            this.authService.credits = snapshot.credits;
        }

        if (snapshot?.walkthroughs !== undefined) {
            this.authService.walkthroughs = snapshot.walkthroughs;
        }

        const snapshotRoles = this.normalisePermissionList(snapshot?.roles);
        if (snapshotRoles !== null) {
            this.permissionsService.roles = snapshotRoles;
        } else {
            this.permissionsService.roles = [];
        }

        const snapshotPermissions = this.normalisePermissionList(snapshot?.permissions);
        if (snapshotPermissions !== null) {
            this.permissionsService.permissions = snapshotPermissions;
        } else {
            this.permissionsService.permissions = [];
        }

        this.storageService.remove(this.devTokenKey);
        this.storageService.remove(this.impersonatingKey);
        this.storageService.remove(this.impersonatedUserNameKey);
        this.storageService.remove(this.impersonatedUserEmailKey);
        this.storageService.remove(this.devSessionKey);
    }

    private captureDeveloperSession(): DeveloperSessionSnapshot
    {
        return {
            user: this.authService.user,
            tenant: this.authService.tenant,
            refreshToken: this.authService.refreshToken,
            credits: this.authService.credits,
            walkthroughs: this.authService.walkthroughs,
            roles: JSON.stringify(this.permissionsService.roles ?? []),
            permissions: JSON.stringify(this.permissionsService.permissions ?? []),
        };
    }

    private getDeveloperSessionSnapshot(): DeveloperSessionSnapshot | null
    {
        const raw = this.storageService.read(this.devSessionKey);

        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private normalisePermissionList(list: string[] | string | String[] | null | undefined): string[] | null
    {
        if (Array.isArray(list)) {
            return this.toStringArray(list);
        }

        if (typeof list === 'string' && list.trim().length > 0) {
            try {
                const parsed = JSON.parse(list);

                return Array.isArray(parsed) ? this.toStringArray(parsed) : null;
            } catch {
                return null;
            }
        }

        return null;
    }

    private toStringArray(values: Array<string | String> | null | undefined): string[]
    {
        return (values ?? []).map(value => String(value));
    }
}

